import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { useTradingPause } from '@flipster/features/src/shared/trading-pause/state';

import TpSlCol from '../../../common/tpsl-col';
import { openPositionRowDataBunja } from '../row-bunja';
import { openPositionTpslCellBunja } from '../tpsl-cell-bunja';

interface Props {
  tpSlType: 'tp' | 'sl';
}

/**
 * TpSlCol은 pending 행들과 공유되는 presentational 컴포넌트라 인터페이스를 유지하고,
 * 이 wrapper가 행 데이터 레이어를 구독해 prop을 전달한다.
 *
 * positionAtom을 통째로 구독하면 pnl tick마다 리렌더돼 Set 버튼까지 매 tick 리렌더되므로,
 * TpslButton이 실제로 쓰는 identity(symbol·positionId=parentId)만 primitive로 구독하고,
 * analytics에 필요한 변동 값은 onManageClick(클릭 시 source에서 fresh read)으로 위임한다.
 */
const TpSlCell = ({ tpSlType }: Props) => {
  const {
    symbol,
    pricePrecisionAtom,
    isReadyAtom,
    positionIdAtom,
    isAirdropAtom,
    marginTypeAtom,
    takeProfitAtom,
    stopLossAtom,
  } = useBunja(openPositionRowDataBunja);
  const { fireManageClickAtom } = useBunja(openPositionTpslCellBunja);

  const isReady = useAtomValue(isReadyAtom);
  // instrument 객체는 매 tick 새로 오므로 통째로 구독하지 않고 필요한 pricePrecision(숫자)만 구독한다.
  const precision = useAtomValue(pricePrecisionAtom);
  // TpslButton identity에 필요한 건 positionId(=parentId)뿐 — 통짜 position 대신 primitive만 구독한다.
  const positionId = useAtomValue(positionIdAtom);
  const isAirdrop = useAtomValue(isAirdropAtom);
  const marginType = useAtomValue(marginTypeAtom);
  const price = useAtomValue(tpSlType === 'tp' ? takeProfitAtom : stopLossAtom);
  const { isPaused } = useTradingPause(symbol);

  const fireManageClick = useSetAtom(fireManageClickAtom);
  const onManageClick = useCallback(
    () => fireManageClick(tpSlType === 'tp' ? 'Take profit' : 'Stop loss'),
    [fireManageClick, tpSlType],
  );

  if (!isReady || !positionId) return null;

  return (
    <TpSlCol
      positionType='open'
      parentId={positionId}
      tpSlType={tpSlType}
      symbol={symbol}
      price={price}
      precision={precision}
      disabled={isPaused}
      isAirdrop={isAirdrop}
      marginType={marginType}
      isReduceOnly={false}
      onManageClick={onManageClick}
    />
  );
};

export default TpSlCell;
